import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, QrCode, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CopyButton, Loader, fmt, StatusBadge, fmtDateTime, EmptyState } from "@/components/shared";

const QR = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=180x180&bgcolor=0A0F1D&color=00F0FF&data=${encodeURIComponent(data)}`;

export default function Deposit() {
  const [settings, setSettings] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [network, setNetwork] = useState("TRC20");
  const [amount, setAmount] = useState("");
  const [hash, setHash] = useState("");
  const [note, setNote] = useState("");
  const [screenshot, setScreenshot] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [s, d] = await Promise.all([api.get("/settings/public"), api.get("/deposits")]);
    setSettings(s.data); setDeposits(d.data);
  };
  useEffect(() => { load(); }, []);

  const wallet = settings ? (network === "TRC20" ? settings.trc20_wallet_address : settings.bep20_wallet_address) : "";

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) return toast.error("Only JPG, PNG, WEBP allowed");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max file size is 5MB");
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt < settings.minimum_deposit) return toast.error(`Minimum deposit is ${settings.minimum_deposit} USDT`);
    if (amt > settings.maximum_deposit) return toast.error(`Maximum deposit is ${settings.maximum_deposit} USDT`);
    if (!hash.trim()) return toast.error("Transaction hash is required");
    if (!screenshot) return toast.error("Payment screenshot is required");
    setLoading(true);
    try {
      await api.post("/deposits", { amount: amt, network, transaction_hash: hash, screenshot_url: screenshot, note });
      toast.success("Deposit submitted successfully. Your transaction is pending review.");
      setAmount(""); setHash(""); setNote(""); setScreenshot("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (!settings) return <Loader />;
  const presets = [10, 20, 30, 40, 50].filter((v) => v >= settings.minimum_deposit && v <= settings.maximum_deposit);

  return (
    <div className="space-y-6" data-testid="deposit-page">
      <div>
        <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Deposit</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Add Funds</h1>
        <p className="text-sm text-slate-400 mt-2">Send USDT to the address below, then submit your transaction details. Deposits range from {fmt(settings.minimum_deposit)} to {fmt(settings.maximum_deposit)} USDT.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6 space-y-5">
          <div className="flex gap-3">
            {["TRC20", "BEP20"].map((n) => (
              <button key={n} data-testid={`deposit-network-${n}`} onClick={() => setNetwork(n)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${network === n ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>
                {n === "TRC20" ? "USDT · TRC20 (Tron)" : "USDT · BEP20 (BNB)"}
              </button>
            ))}
          </div>
          <div className="bg-slate-900/60 rounded-xl p-5 border border-cyan-500/10">
            <div className="flex flex-col sm:flex-row gap-5 items-center">
              <img src={QR(wallet)} alt="QR" className="w-40 h-40 rounded-lg border border-cyan-500/20" data-testid="deposit-qr" />
              <div className="flex-1 w-full">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Network</p>
                <p className="text-sm text-slate-200 mb-3">{network === "TRC20" ? "Tron (TRC20)" : "BNB Smart Chain (BEP20)"}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Wallet Address</p>
                <p className="font-mono text-xs text-cyan-300 break-all mb-3" data-testid="deposit-wallet-address">{wallet}</p>
                <CopyButton value={wallet} testId="wallet-copy-btn" label="Copy Address" />
              </div>
            </div>
          </div>
          <p className="text-xs text-amber-200/70">Only send USDT on the selected network to this address. Sending other assets or using the wrong network may result in permanent loss.</p>
        </div>

        <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-heading text-lg text-white">Submit Deposit</h3>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Amount (USDT)</label>
            <input data-testid="deposit-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" placeholder={`${settings.minimum_deposit} - ${settings.maximum_deposit}`} />
            <div className="flex flex-wrap gap-2 mt-2">
              {presets.map((v) => (
                <button type="button" key={v} data-testid={`deposit-preset-${v}`} onClick={() => setAmount(String(v))}
                  className="px-3 py-1 rounded-lg text-xs bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 hover:bg-cyan-500/20">{v}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Transaction Hash</label>
            <input data-testid="deposit-hash" value={hash} onChange={(e) => setHash(e.target.value)}
              className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 font-mono text-sm focus:border-cyan-400 focus:outline-none" placeholder="0x... / TxID" />
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Payment Screenshot</label>
            {screenshot ? (
              <div className="relative">
                <img src={screenshot} alt="Preview" className="w-full h-40 object-cover rounded-lg border border-cyan-500/20" />
                <button type="button" onClick={() => setScreenshot("")} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-slate-900/80 flex items-center justify-center text-slate-200"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-cyan-500/20 rounded-lg cursor-pointer hover:border-cyan-500/40 transition-colors">
                <Upload className="w-6 h-6 text-cyan-400 mb-2" />
                <span className="text-xs text-slate-400">Click to upload (JPG, PNG, WEBP · max 5MB)</span>
                <input data-testid="deposit-screenshot" type="file" accept="image/*" className="hidden" onChange={onFile} />
              </label>
            )}
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Note (optional)</label>
            <input data-testid="deposit-note" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" placeholder="Any reference" />
          </div>
          <button type="submit" disabled={loading} data-testid="deposit-submit"
            className="btn-glow w-full py-3 rounded-lg font-semibold text-slate-950 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit Deposit
          </button>
        </form>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="font-heading text-lg text-white mb-5">Deposit History</h3>
        {deposits.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead><tr className="text-left text-slate-500 border-b border-white/5">
                <th className="pb-3 font-medium">Amount</th><th className="pb-3 font-medium">Network</th><th className="pb-3 font-medium">Hash</th><th className="pb-3 font-medium">Status</th><th className="pb-3 font-medium">Date</th>
              </tr></thead>
              <tbody>
                {deposits.map((d) => (
                  <tr key={d.id} className="border-b border-white/5 last:border-0">
                    <td className="py-3 font-mono text-white">{fmt(d.amount)}</td>
                    <td className="py-3 text-slate-300">{d.network}</td>
                    <td className="py-3 font-mono text-xs text-slate-500 max-w-[120px] truncate">{d.transaction_hash}</td>
                    <td className="py-3"><StatusBadge status={d.status} />{d.status === "rejected" && d.rejection_reason && <p className="text-xs text-red-400 mt-1">{d.rejection_reason}</p>}</td>
                    <td className="py-3 text-slate-500">{fmtDateTime(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState title="No deposits yet" subtitle="Submit your first deposit above." />}
      </div>
    </div>
  );
}
